// src/components/opportunity/OpportunityProposalPreviewModal.tsx
//
// Preview modal for a freshly-rendered proposal PDF. The Blob is
// rendered upstream (by `renderProposalPdfToBlob`) and handed in; we
// build a blob URL, drop it into a native <iframe>, and offer the
// user three actions: save-to-opportunity (commits via the sign +
// commit flow without re-rendering), close, or implicit-discard (just
// closing the modal — nothing is persisted).
//
// We pick a native <iframe> over @react-pdf/renderer's <PDFViewer>
// because every major browser renders application/pdf in an iframe
// natively, and the iframe lets us reuse the exact same Blob we'll
// upload — no risk of the preview diverging from the saved artifact.
// Blob URLs are revoked on close to avoid leaking the rendered PDF in
// memory.

import { useEffect, useMemo, useState } from "react";
import type {
  Opportunity,
  OpportunityRevision,
} from "@/types/opportunity";
import { uploadRenderedOpportunityDocumentPackage } from "@/components/opportunity/pdf/generateProposalPdf";
import type { OpportunityDocumentArchetype } from "@/lib/opportunity/documentArchetypes";
import { OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS } from "@/lib/opportunity/documentArchetypes";

export type OpportunityProposalPreviewModalProps = {
  open: boolean;
  blob: Blob | null;
  opportunity: Opportunity;
  revision: OpportunityRevision | null;
  archetype?: OpportunityDocumentArchetype;
  alternateKey?: string | null;
  alternateLabel?: string | null;
  /** Called after a successful save with the new generated-output id. */
  onSaved: (outputId: string) => void;
  /** Called when the user dismisses the modal (without saving). */
  onClose: () => void;
};

export function OpportunityProposalPreviewModal(
  props: OpportunityProposalPreviewModalProps,
) {
  const {
    open,
    blob,
    opportunity,
    revision,
    archetype = "proposal",
    alternateKey,
    alternateLabel,
    onSaved,
    onClose,
  } = props;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const familyLabel = OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS[archetype];

  // Build a blob URL once per blob; revoke when the modal closes or
  // the blob is swapped out. Browsers cap the lifetime to the
  // document, but explicit revocation matters when the user
  // previews several packages in a single session.
  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!open) return null;

  async function save() {
    if (!blob) return;
    setSaving(true);
    setError(null);
    try {
      const r = await uploadRenderedOpportunityDocumentPackage({
        opportunity,
        revision,
        blob,
        archetype,
        alternateKey,
        alternateLabel,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      onSaved(r.outputId);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to save ${familyLabel.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60 px-4 py-6 sm:px-6">
      <div className="flex h-full w-full max-w-6xl mx-auto flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-3 dark:border-zinc-700">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">
              {familyLabel} preview
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400 truncate">
              {opportunity.customer_company ||
                opportunity.customer_name ||
                opportunity.project_name ||
                "Opportunity"}
              {revision ? (
                <>
                  <span className="mx-1">·</span>
                  Revision {revision.revision_number}
                </>
              ) : null}
              {alternateLabel ? (
                <>
                  <span className="mx-1">·</span>
                  {alternateLabel}
                </>
              ) : null}
            </p>
            {error ? (
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !blob}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save to opportunity"}
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 dark:bg-zinc-950">
          {blobUrl ? (
            <iframe
              key={blobUrl}
              src={blobUrl}
              title={`${familyLabel} preview`}
              className="h-full w-full border-0"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
              No preview available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
